import React, { useMemo, useState } from 'react';
import { getMaxRotateForImage } from './decalEngine';

/**
 * User-editable image array for Dead Insects (flies, moths, beetles, roaches).
 * Clean root-relative URLs pointing to local artwork assets.
 */
export const DEAD_INSECT_IMAGES: (string | { src: string; maxRotate?: number })[] = [
  '/Images/Dead Insects/fly.png',
  '/Images/Dead Insects/roach.png',
  '/Images/Dead Insects/maggot.png',
  { src: '/Images/Dead Insects/maggots.png', maxRotate: 10 }
];

export interface DeadInsectProps {
  x?: number; // percentage (0 - 100)
  y?: number; // percentage (0 - 100)
  rotation?: number; // degrees
  /** Custom +/- max rotation clamp (0 - 360). E.g. 10 for +/- 10 deg */
  maxRotate?: number;
  scale?: number;
  opacity?: number;
  /** Single image override */
  imageSrc?: string;
  /** Array of image URLs or image descriptors to choose from randomly */
  images?: (string | { src: string; maxRotate?: number })[];
  /** Custom drop shadow filter override (defaults to physical insect depth) */
  dropShadow?: string;
  /** Custom width size (default: 90px) */
  size?: number | string;
  className?: string;
  style?: React.CSSProperties;
}

export const DeadInsect: React.FC<DeadInsectProps> = ({
  x = 50,
  y = 50,
  rotation = 0,
  maxRotate,
  scale = 1,
  opacity = 1,
  imageSrc,
  images,
  dropShadow,
  size = 90,
  className = '',
  style
}) => {
  const pool = images && images.length > 0 ? images : DEAD_INSECT_IMAGES;
  const chosen = useMemo(() => {
    if (imageSrc) return { src: imageSrc, maxRotate };
    const idx = Math.floor(Math.random() * pool.length);
    const item = pool[idx];
    if (typeof item === 'string') {
      return { src: item, maxRotate };
    }
    return { src: item.src, maxRotate: item.maxRotate ?? maxRotate };
  }, [imageSrc, pool, maxRotate]);

  const [currentSrc, setCurrentSrc] = useState(chosen.src);

  // Softer, subtle insect drop shadow (reduced opacity & blur)
  const insectFilter = dropShadow || 'drop-shadow(2px 3px 4px rgba(0, 0, 0, 0.35))';

  // Determine effective maxRotate:
  // maggots.png has maxRotate: 10 (+/- 10 deg); others allow full 360 deg or custom maxRotate
  const effectiveMaxRotate = useMemo(() => {
    return getMaxRotateForImage(currentSrc, chosen.maxRotate ?? maxRotate, 'insect');
  }, [currentSrc, chosen.maxRotate, maxRotate]);

  // Clamp rotation within [-effectiveMaxRotate, +effectiveMaxRotate]
  const normalizedRotation = ((rotation % 360) + 540) % 360 - 180;
  const clampedRotation = effectiveMaxRotate < 360
    ? Math.max(-effectiveMaxRotate, Math.min(effectiveMaxRotate, normalizedRotation))
    : rotation;

  return (
    <div
      className={`random-thing decal-insect ${className}`}
      style={{
        position: 'absolute',
        left: `${x}%`,
        top: `${y}%`,
        transform: `translate(-50%, -50%) rotate(${clampedRotation}deg) scale(${scale})`,
        opacity: 1,
        pointerEvents: 'none',
        userSelect: 'none',
        mixBlendMode: 'normal',
        filter: insectFilter,
        ...style
      }}
    >
      <img
        src={currentSrc}
        alt="dead insect"
        style={{
          display: 'block',
          width: typeof size === 'number' ? `${size}px` : size,
          height: 'auto',
          pointerEvents: 'none'
        }}
        onError={() => {
          if (!currentSrc.startsWith('/') && !currentSrc.startsWith('http')) {
            setCurrentSrc(`/${currentSrc}`);
          }
        }}
      />
    </div>
  );
};

export default DeadInsect;
